import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Box, BoxProps } from '@tanium/react-box';
import styled from '@tanium/react-emotion-9';
import { Icon } from '@tanium/react-graphics';
import { Stack, StackChild } from '@tanium/react-stack';
import { SimpleTooltip } from '@tanium/react-tooltip';
import { useResizeObserver } from '@tanium/react-use-resize-observer';
import { ThemeFontWeights } from '@tanium/theme-data';

import { useAggregatedRef } from '../../hooks/useAggregatedRef';
import { ExtendAndOverride } from '../../utils/ExtendAndOverride';

const FONT_SIZE = 12;
const LINE_HEIGHT = 1.5;

export const MaxLinesNoOverflow = styled('div')(
  ({ lineClamp }: { lineClamp?: number }) =>
    lineClamp && {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      WebkitLineClamp: lineClamp,
      WebkitBoxOrient: 'vertical',
      display: '-webkit-box',
    },
);

interface EmptyStateOwnProps {
  icon?: Icon;
  iconColor?: BoxProps['color'];
  headerContent?: React.ReactNode;
}

export type EmptyStateProps = ExtendAndOverride<EmptyStateOwnProps, Omit<BoxProps, 'css'>>;

export const EmptyState = ({
  icon: IconComponent,
  iconColor,
  headerContent,
  children,
  ...boxProps
}: EmptyStateProps): JSX.Element => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [lineClamp, setLineClamp] = useState<number | undefined>();

  const updateOverflowStatus = useCallback(
    (element: Element) => setIsOverflowing(element.scrollHeight > element.clientHeight),
    [],
  );

  const availableContentRef = useResizeObserver(
    useCallback(
      ({ contentRect: { height: availableHeight } }) =>
        setLineClamp(Math.max(1, Math.floor(availableHeight / (FONT_SIZE * LINE_HEIGHT)))),
      [],
    ),
  );

  const noOverflowRef = useRef<HTMLDivElement>();
  const noOverflowResizeRef = useResizeObserver(
    useCallback(({ target }) => updateOverflowStatus(target), [updateOverflowStatus]),
  );
  const noOverflowRefs = useAggregatedRef(
    useMemo(() => [noOverflowRef, noOverflowResizeRef], [noOverflowRef, noOverflowResizeRef]),
  );

  useLayoutEffect(() => {
    if (noOverflowRef.current) {
      updateOverflowStatus(noOverflowRef.current);
    }
  }, [children, updateOverflowStatus]);

  return (
    <Stack
      px={2}
      py={1}
      border={1}
      borderRadius={1}
      {...boxProps}
      // specify stack-specific props after spread
      itemSpacing={1}
      direction="column"
      alignItems="center"
    >
      <StackChild grow={1} display="flex" alignItems="flex-end">
        {!!(IconComponent && iconColor) && <Box as={IconComponent} size="20px" color={iconColor} />}
      </StackChild>
      {headerContent}
      <StackChild grow={1} innerRef={availableContentRef} overflow="hidden" width="100%">
        {children && (
          <Box
            fontWeight={ThemeFontWeights.Medium}
            textAlign="center"
            fontSize={FONT_SIZE}
            lineHeight={`${LINE_HEIGHT}`}
          >
            <SimpleTooltip content={isOverflowing && children} placement="top">
              {({ ref }: { ref: React.MutableRefObject<HTMLDivElement> }) => (
                <Box ref={ref} overflow="hidden">
                  <MaxLinesNoOverflow lineClamp={lineClamp} innerRef={noOverflowRefs}>
                    {children}
                  </MaxLinesNoOverflow>
                </Box>
              )}
            </SimpleTooltip>
          </Box>
        )}
      </StackChild>
    </Stack>
  );
};
